import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, HostListener, ElementRef, ViewChild } from "@angular/core";
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  imports: [
    CommonModule,
    TranslateModule
  ],
  selector: "app-modal",
  standalone: true,
  styleUrls: ["modal.component.scss"],
  templateUrl: "modal.component.html"
})
export class ModalComponent implements OnInit, OnChanges {
  // Every app-modal stays in the DOM and is only hidden, so several instances
  // coexist on a page. A shared id would make aria-labelledby resolve against
  // whichever one the browser finds first — in practice the header's modal, so
  // every dialog announced itself as "Add different company environment".
  private static instanceCount: number = 0;

  /** Re-entrance guard for closeModal(); see the comment there. */
  private isClosing: boolean = false;

  @ViewChild('modalContainer', { static: false }) modalContainer!: ElementRef;

  public readonly titleId: string = `modal-title-${++ModalComponent.instanceCount}`;

  isActiveThis: boolean = false;
  titleThis: string;
  bodyThis: string;

  @Input() title: string;
  @Input() bodyContent: string;
  @Input() confirmCallback: Function;
  // Confirm-footer styling: danger renders the Business-Unit delete design
  // (trash icon block + red Delete button). confirmText/cancelText override
  // the default button labels (translate keys).
  @Input() confirmDanger: boolean = false;
  @Input() confirmText: string;
  @Input() cancelText: string;
  @Input() size: 'small' | 'medium' | 'large' | 'xlarge' = 'medium';
  @Input() closeOnOverlayClick: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() showCloseButton: boolean = true;
  @Output() closer: EventEmitter<any> = new EventEmitter<any>();
  @Output() opened: EventEmitter<any> = new EventEmitter<any>();

  // The `title` @Input is a translate key/text rendered by the header <h5>.
  // Because `title` is also a native HTML attribute, a static `title="KEY"` on
  // <app-modal> reflects the raw value onto the host element's native title
  // (browser tooltip / accessible name). Bind the native attribute to undefined
  // so it is never surfaced untranslated.
  @HostBinding('attr.title') hostTitle: string | undefined;

  constructor(private elementRef: ElementRef, private translate: TranslateService) {}

  /**
   * BUG-086: the body was rendered raw, so a caller passing a translate KEY got the key on
   * screen - the brand-asset removal dialog read "SETTINGS.REMOVE_LOGO_CONFIRM" where the warning
   * should have been. The title has always gone through the pipe; only the body did not.
   *
   * Both call styles have to keep working: 23 of the 24 current callers interpolate the pipe
   * themselves and pass finished prose, one passes a key. TranslateService.instant returns its
   * input unchanged when there is no such key, so prose passes through untouched and a key
   * resolves - which also means a caller cannot get this wrong in future.
   */
  private resolveBody(value: string): string {
    if (!value) {
      return value;
    }
    return this.translate.instant(value);
  }

  ngOnInit() {
    this.titleThis = this.title;
    this.bodyThis = this.resolveBody(this.bodyContent);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.title) {
        this.titleThis = changes.title.currentValue;
    }

    if (changes.bodyContent) {
        this.bodyThis = this.resolveBody(changes.bodyContent.currentValue);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (this.isActiveThis && this.closeOnEscape) {
      this.closeModal();
    }
  }

  @HostListener('click', ['$event'])
  onOverlayClick(event: MouseEvent) {
    // Close modal only if clicking on the overlay (modal background), not the modal content
    if (this.closeOnOverlayClick && 
        event.target === this.elementRef.nativeElement.querySelector('.modal-overlay')) {
      this.closeModal();
    }
  }

  openModal() {
    // Deliberately NOT re-resolving the body here: at this point the @Input still holds the
    // previous value, because a host that sets it and opens in the same tick has not let Angular
    // write the input yet. ngOnChanges resolves it on the change-detection pass that follows,
    // before anything is painted - which is what makes one dialog reusable for logo and favicon.
    this.isActiveThis = true;
    this.opened.emit(null);
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Focus management for accessibility
    setTimeout(() => {
      const focusableElements = this.modalContainer?.nativeElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements && focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }, 100);
  }

  closeModal() {
    // The (closer) output exists so a host can react to the dialog closing, and the app's
    // most common handler for it closes that same dialog — `(closer)="closeBuModal()"` where
    // closeBuModal() calls closeModal(). That re-enters here from inside the emit, so a
    // single close recursed until the stack gave out ("Maximum call stack size exceeded" on
    // every save or close of the Rights step editor, and the same shape in 31 other
    // bindings). Guarding re-entrance rather than early-returning on an already-closed modal
    // keeps the emit for hosts that call closeModal() defensively to reset their own state.
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;

    try {
      this.isActiveThis = false;
      this.closer.emit(undefined);

      // Restore body scroll
      document.body.style.overflow = 'auto';
    } finally {
      this.isClosing = false;
    }
  }

  confirmClick() {
    if (this.confirmCallback) {
      this.confirmCallback();
    }
  }

  get confirmLabel(): string {
    return this.confirmText || (this.confirmDanger ? "GENERIC.DELETE" : "GENERIC.CONFIRM");
  }

  get cancelLabel(): string {
    return this.cancelText || "GENERIC.CANCEL";
  }

  // Prevent modal content clicks from closing the modal
  onModalContentClick(event: MouseEvent) {
    event.stopPropagation();
  }

  // Get modal size classes
  getModalSizeClass(): string {
    const sizeClasses = {
      small: 'modal-small',
      medium: 'modal-medium', 
      large: 'modal-large',
      xlarge: 'modal-xlarge'
    };
    return sizeClasses[this.size] || sizeClasses.medium;
  }
}